/** GxKpiTile and its pillar frame (screens §P4.4): a big value, its name and one note line, an optional sparkline, then rows. */
import type { ReactNode } from "react";
import { GxPill, type PillTone } from "../theme/GxPill";
import { GxSparkline } from "../theme/Sparkline";

/** "14.5%" → the number big and the % small, as the mockup sets it. */
function splitUnit(text: string): { readonly main: string; readonly unit: string } {
  return text.endsWith("%") ? { main: text.slice(0, -1), unit: "%" } : { main: text, unit: "" };
}

export function GxPillar({ id, title, color, chip, children }: {
  readonly id: string; readonly title: ReactNode; readonly color: string; readonly chip: { readonly tone: PillTone; readonly text: ReactNode } | null; readonly children: ReactNode;
}) {
  return (
    <section className="gx-card gx-pillar" data-testid={`cockpit.pillar.${id}`}>
      <div className="gx-pillar-top">
        <div className="gx-eyebrow gx-pillar-title"><i style={{ background: color }} />{title}</div>
        {chip === null ? null : <GxPill tone={chip.tone}>{chip.text}</GxPill>}
      </div>
      {children}
    </section>
  );
}

export function GxKpiTile({ testId, value, name, note, spark }: {
  readonly testId: string; readonly value: string; readonly name: ReactNode; readonly note: string | null;
  readonly spark: { readonly values: readonly number[]; readonly color: string; readonly reference?: number } | null;
}) {
  const { main, unit } = splitUnit(value);
  // One template for every tile: the name, the value (sparkline beside it), then one line of comparison. Definitions live in the glossary popover on the name.
  return (
    <div className="gx-kpi">
      <div className="gx-kpi-n">{name}</div>
      <div className="gx-kpi-main">
        <div className="gx-kpi-v gx-num" data-testid={testId}>{main}{unit === "" ? null : <span>{unit}</span>}</div>
        {spark === null || spark.values.length < 2 ? null : <GxSparkline values={spark.values} color={spark.color} {...(spark.reference === undefined ? {} : { reference: spark.reference })} />}
      </div>
      {note === null ? null : <div className="gx-kpi-delta">{note}</div>}
    </div>
  );
}

/** A row the engine has not produced yet (a dash) is left out: the tile shows what exists. */
const NO_VALUE = "—";

export function GxKpiRows({ rows: all }: { readonly rows: readonly { readonly key: string; readonly label: ReactNode; readonly value: string; /** A basis line under the name (a run rate), not a definition. */ readonly note?: string }[] }) {
  const rows = all.filter((r) => r.value !== NO_VALUE);
  if (rows.length === 0) return null;
  return (
    <div className="gx-pillar-rows">
      {rows.map((r) => <div key={r.key} className="gx-kv"><span className="gx-muted">{r.label}{r.note === undefined ? null : <small className="gx-faint gx-kv-note" data-testid={`cockpit.kpi.${r.key}.note`}>{r.note}</small>}</span><b className="gx-num" data-testid={`cockpit.kpi.${r.key}`}>{r.value}</b></div>)}
    </div>
  );
}
